/**
 * AdhocSessionRunner — the task-less USER SCRATCH pod agent loop (Sessions view
 * "Launch ad-hoc session"). Claudespace v3, `Workspace.purpose = "adhoc"`.
 *
 * Unlike the ProjectSessionRunner (autonomous pm) it runs NO autonomous loop:
 * it spawns the `claude` CLI under a pseudo-terminal in interactive/prefill mode
 * (empty input box, no auto-submit) and relays raw terminal I/O to the S2 relay,
 * so the web Sessions terminal (S5) renders it and forwards keystrokes back. The
 * human drives it directly — there is no task, plan, or Conveyor MCP tool
 * surface. Connect / register / heartbeat / idle are shared with the project
 * runner; the difference is the interactive PTY spawn.
 *
 * Idle-sleep is server-owned (decideFate + activity/tunnel holds): an idle
 * adhoc pod sleeps after the idle window like any workspace; the human wakes it
 * from the Sessions list. The agent's own idle timer is the secondary backstop.
 */
import { AgentConnection, type AgentConnectionConfig } from "../connection/agent-connection.js";
import { Lifecycle, DEFAULT_LIFECYCLE_CONFIG, type LifecycleConfig } from "./lifecycle.js";
import { resolveSessionStart } from "../execution/query-executor.js";
import { resolvePlaywrightMcpServer } from "../execution/playwright-mcp.js";
import { sampleKeyUsage } from "../execution/usage-sampler.js";
import { PtyHarness } from "../harness/pty/index.js";
import { resolveTuiAdapter } from "../harness/pty/adapters/index.js";
import { TUI_KINDS, type TuiKind } from "../harness/pty/adapters/types.js";
import type { AgentHarness, HarnessQueryOptions, PtyBridge } from "../harness/types.js";
import { DEFAULT_SONNET_MODEL, type PtyChatEventPayload } from "@project/shared";

const ADHOC_SYSTEM_NOTE =
  "You are running in an ad-hoc Conveyor scratch pod — an interactive terminal " +
  "on the project's repository checked out at its default branch. There is no " +
  "task or plan; help the human with whatever they ask directly.";

export interface AdhocSessionRunnerConfig {
  connection: AgentConnectionConfig;
  projectId: string;
  /**
   * Stable `Workspace` row id — the Claude session lineage key. Deliberately
   * NOT `connection.sessionId`: that's the `WorkspaceSession` row, which is
   * re-minted (fresh-session semantics) on every user-initiated wake, so
   * keying resume on it would never find the prior GCS-FUSE transcript.
   */
  workspaceId: string;
  /** Repo checkout the interactive CLI runs in. */
  workspaceDir: string;
  /** Model override; falls back to CONVEYOR_ADHOC_MODEL env, then the default. */
  model?: string;
  lifecycle?: Partial<LifecycleConfig>;
}

export interface AdhocSessionRunnerCallbacks {
  onEvent?: (event: Record<string, unknown>) => void;
}

/** AgentConnection surface the adhoc runner needs (injectable for tests). */
export interface AdhocRunnerConnection {
  connect(): Promise<void>;
  disconnect(): void;
  call(method: "connectAgent", payload: { sessionId: string }): Promise<unknown>;
  sendEvent(event: { type: string; [key: string]: unknown }): void;
  sendHeartbeat(): void;
  emitStatus(status: string): Promise<void>;
  onStop(callback: () => void): void;
  refreshTaskTokenFromBootstrap(): Promise<boolean>;
  sendPtyOutput(data: string, dims?: { cols: number; rows: number }): void;
  sendPtyChatEvent(event: PtyChatEventPayload): void;
  onPtyInput(handler: (data: string) => void): () => void;
  onPtyResize(handler: (cols: number, rows: number) => void): () => void;
}

/** Which TUI this adhoc pod runs — from the bootstrap bundle's CONVEYOR_TUI.
 *  Unknown values throw (fail loudly): a typo'd config must not silently run
 *  Claude. */
export function resolveAdhocTui(env: NodeJS.ProcessEnv): TuiKind {
  const raw = env.CONVEYOR_TUI ?? "claude-code";
  if ((TUI_KINDS as readonly string[]).includes(raw)) return raw as TuiKind;
  throw new Error(`Unknown TUI "${raw}" in CONVEYOR_TUI (expected: ${TUI_KINDS.join(", ")})`);
}

/** Adapt an AdhocRunnerConnection into the harness-neutral PtyBridge. */
export function buildAdhocPtyBridge(connection: AdhocRunnerConnection): PtyBridge {
  return {
    sendOutput: (data, dims) => connection.sendPtyOutput(data, dims),
    sendChatEvent: (event) => connection.sendPtyChatEvent(event),
    onInput: (handler) => connection.onPtyInput(handler),
    onResize: (handler) => connection.onPtyResize(handler),
  };
}

/** The interactive-spawn options for an ad-hoc scratch TUI (no task context). */
export function buildAdhocQueryOptions(
  workspaceDir: string,
  model: string,
  abortController: AbortController,
  // Deterministic session start (fresh `sessionId` or `resume` target). Without
  // one, PtySession.start() throws "requires options.sessionId or a resume
  // target" and the pool crash-loops the runner — the interactive PTY never
  // spawns. Mutually exclusive: exactly one of the two is ever set.
  session?: { sessionId?: string; resume?: string },
  // Environment for PATH-based binary resolution (playwright-mcp). Injectable
  // so tests are deterministic regardless of what the host has baked on PATH
  // (claudespace pods ship /usr/local/bin/playwright-mcp; dev machines don't).
  env: NodeJS.ProcessEnv = process.env,
): HarnessQueryOptions {
  return {
    model,
    systemPrompt: { type: "preset", preset: "claude_code", append: ADHOC_SYSTEM_NOTE },
    appendSystemPrompt: ADHOC_SYSTEM_NOTE,
    cwd: workspaceDir,
    // Human-driven interactive shell — no plan-mode gating, no Conveyor tools.
    permissionMode: "bypassPermissions",
    allowDangerouslySkipPermissions: true,
    tools: { type: "preset", preset: "claude_code" },
    // Browser automation only — the baked playwright-mcp when present.
    mcpServers: (() => {
      const playwright = resolvePlaywrightMcpServer(env);
      return playwright ? { playwright } : {};
    })(),
    settingSources: ["user", "project"],
    // Empty box, unsubmitted: the human types the first prompt themselves.
    promptDelivery: "prefill",
    abortController,
    ...(session?.sessionId ? { sessionId: session.sessionId } : {}),
    ...(session?.resume ? { resume: session.resume } : {}),
  };
}

export class AdhocSessionRunner {
  readonly connection: AdhocRunnerConnection;
  readonly lifecycle: Lifecycle;
  private readonly harness: AgentHarness;

  private readonly config: AdhocSessionRunnerConfig;
  private readonly callbacks: AdhocSessionRunnerCallbacks;
  private readonly abortController = new AbortController();
  private stopped = false;
  private stopResolver: (() => void) | null = null;
  private _finalState: "finished" | "error" | null = null;

  constructor(
    config: AdhocSessionRunnerConfig,
    callbacks: AdhocSessionRunnerCallbacks = {},
    deps?: { connection?: AdhocRunnerConnection; harness?: AgentHarness },
  ) {
    this.config = config;
    this.callbacks = callbacks;
    this.connection = deps?.connection ?? new AgentConnection(config.connection);
    this.harness =
      deps?.harness ??
      new PtyHarness(
        buildAdhocPtyBridge(this.connection),
        resolveTuiAdapter(resolveAdhocTui(process.env)),
      );
    this.lifecycle = new Lifecycle(
      // No git flush: the WIP snapshot machinery is branch/task-shaped; the human
      // commits/pushes explicitly from the interactive shell.
      { ...DEFAULT_LIFECYCLE_CONFIG, gitFlushIntervalMs: 0, ...config.lifecycle },
      {
        onHeartbeat: () => this.connection.sendHeartbeat(),
        onIdleTimeout: () => this.requestStop(),
        onDormantTimeout: () => this.requestStop(),
        onTokenRefresh: () => void this.connection.refreshTaskTokenFromBootstrap().catch(() => {}),
        onGitFlush: () => {},
        onUsageSample: () => void this.sampleAndReportKeyUsage(),
      },
    );
  }

  get finalState(): "finished" | "error" | null {
    return this._finalState;
  }

  get isStopped(): boolean {
    return this.stopped;
  }

  /** Connect, register, spawn the interactive TUI, and relay it until stopped. */
  async run(): Promise<void> {
    try {
      await this.connection.connect();
      this.connection.sendEvent({
        type: "connected",
        sessionId: this.config.connection.sessionId,
        projectId: this.config.projectId,
      });
      this.connection.onStop(() => this.requestStop());
      this.lifecycle.startHeartbeat();
      this.lifecycle.startTokenRefresh();
      this.lifecycle.startUsageSample();

      await this.connection.call("connectAgent", {
        sessionId: this.config.connection.sessionId,
      });
      await this.connection.emitStatus("running");
      this.callbacks.onEvent?.({ type: "adhoc_runner_started", projectId: this.config.projectId });
      this.lifecycle.startIdleTimer();

      await this.runInteractiveTui();
      this._finalState = "finished";
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      process.stderr.write(`[conveyor-agent] Ad-hoc runner failed: ${message}\n`);
      this.connection.sendEvent({ type: "error", message });
      this._finalState = "error";
    } finally {
      this.shutdown();
    }
  }

  /**
   * Drive the `claude` CLI under a PTY. The bridge relays raw stdout to S2/S5
   * and forwards keystrokes back, so consuming the event stream is enough — the
   * loop blocks (keeping the pod alive) until the human exits or stop() aborts.
   */
  private async runInteractiveTui(): Promise<void> {
    const model =
      this.config.model ??
      process.env.CONVEYOR_AGENT_MODEL ??
      process.env.CONVEYOR_ADHOC_MODEL ??
      DEFAULT_SONNET_MODEL;
    // Key the CLI session on the stable workspace id (NOT connection.sessionId
    // — see the field doc on AdhocSessionRunnerConfig.workspaceId): a fresh
    // pod spawns a new session, a woken pod resumes the GCS-FUSE-persisted
    // transcript.
    const session = resolveSessionStart(this.config.workspaceId, this.config.workspaceDir);
    const options = buildAdhocQueryOptions(
      this.config.workspaceDir,
      model,
      this.abortController,
      session,
    );
    try {
      for await (const event of this.harness.executeQuery({ prompt: "", options })) {
        if (this.stopped) break;
        if (event.type === "result" && event.subtype === "error") {
          // Raw-mode adapters surface spawn/exit failures as a result error —
          // fail the run loudly so the Sessions UI shows the reason.
          throw new Error(event.errors.join("\n"));
        }
      }
    } catch (error) {
      // An aborted interactive session is the normal stop path, not a failure.
      if (!this.stopped) throw error;
    }
  }

  stop(): void {
    this.requestStop();
  }

  private requestStop(): void {
    if (this.stopped) return;
    this.stopped = true;
    this.abortController.abort();
    if (this.stopResolver) {
      const resolve = this.stopResolver;
      this.stopResolver = null;
      resolve();
    }
  }

  private async sampleAndReportKeyUsage(): Promise<void> {
    if (this.stopped) return;
    const samples = await sampleKeyUsage(process.env.CLAUDE_CODE_OAUTH_TOKEN);
    for (const sample of samples) {
      this.connection.sendEvent({
        type: "rate_limit_update",
        rateLimitType: sample.rateLimitType,
        utilization: sample.utilization,
        status: sample.status,
      });
    }
  }

  private shutdown(): void {
    this.stopped = true;
    this.lifecycle.destroy();
    this.connection.sendEvent({ type: "shutdown", reason: this._finalState ?? "finished" });
    this.connection.disconnect();
  }
}
